import { ClipboardCopy, Download, ExternalLink, ZoomInIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import React, { ReactElement, useEffect, useState } from 'react';
import { useRestClient } from '../../api/RestClientContext';
import { notify } from '../../shared/utils/notification';
import './SetupSteps.css';

export interface LinkTransform {
    type: 'happcrypto';
    endpoint: string;
}

function useCachedLink(link: string | undefined, linkTransform: LinkTransform | undefined) {
    const client = useRestClient();

    const { data, isFetching, error } = useQuery({
        queryKey: ['resolvedLink', link, linkTransform?.type],
        queryFn: async () => {
            const response = await client.happCrypto(link!);
            if (!response.success || !response.result) {
                throw new Error(response.message || 'Не удалось подготовить ссылку');
            }
            return response.result;
        },
        enabled: !!link && !!linkTransform,
        staleTime: Infinity,
        retry: false,
    });

    useEffect(() => {
        if (error) notify.error((error as Error).message || 'Не удалось подготовить ссылку');
    }, [error]);

    return { resolvedLink: linkTransform ? data : link, loading: isFetching };
}

export const StepDownloadApp: React.FC<{
    appName: string;
    storeName: string;
    link: string;
    img?: string;
    onOpenImage?: (img: string) => void;
    onExpandContent?: () => void;
}> = ({ appName, storeName, link, img, onOpenImage, onExpandContent }) => {
    return (
        <div>
            <div>Скачайте приложение из <b>{storeName}</b>:</div>
            <a
                className="vpn-download-btn"
                href={link}
                target="_blank"
                rel="noopener noreferrer"
            >
                <Download color='var(--color-on-accent)' size={17} />
                Скачать {appName}
                <ExternalLink color='var(--color-on-accent)' size={14} className="vpn-download-btn-ext" />
            </a>
            <StepImagePreview img={img} onOpenImage={onOpenImage} onExpandContent={onExpandContent} />
        </div>
    );
};

export const StepCopyLink: React.FC<{
    copyLink?: string;
    linkTransform?: LinkTransform;
    img?: string;
    onOpenImage?: (img: string) => void;
    onExpandContent?: () => void;
}> = ({ copyLink, img, linkTransform, onExpandContent, onOpenImage }) => {
    const { resolvedLink, loading } = useCachedLink(copyLink, linkTransform);

    const handleClick = async () => {
        if (!resolvedLink) { notify.error('Ссылка недоступна'); return; }
        await navigator.clipboard.writeText(resolvedLink);
        notify.success('Ссылка скопирована в буфер обмена!');
    };

    return (
        <>
            <button className="vpn-import-btn" onClick={handleClick} disabled={loading}>
                {loading ? 'Подготовка ссылки...' : 'Нажмите, чтобы скопировать ссылку'}
                {!loading && <ClipboardCopy size={16} color='var(--color-on-accent)'/>}
            </button>
            <StepImagePreview img={img} onOpenImage={onOpenImage} onExpandContent={onExpandContent} />
        </>
    );
};

interface StepImportLinkProps {
    importUri: string;
    link?: string;
    linkTransform?: LinkTransform;
    fallbackCopyInstruction?: ReactElement[];
    onExpandContent?: () => void,
}

type ImportState = 'idle' | 'confirming' | 'fallback';

export const StepImportLink: React.FC<StepImportLinkProps> = ({
    importUri,
    link,
    linkTransform,
    fallbackCopyInstruction = [],
    onExpandContent
}) => {
    const [state, setState] = useState<ImportState>('idle');
    const { resolvedLink, loading } = useCachedLink(link, linkTransform);

    const transition = (next: ImportState) => {
        setState(next);
        onExpandContent?.();
    };

    const handleImport = async () => {
        if (!resolvedLink) { notify.error('Ссылка недоступна'); return; }
        const finalLink = linkTransform ? resolvedLink : `${importUri}${encodeURI(resolvedLink)}`;
        console.log("Final link:" + finalLink);

        try {
            window.open(finalLink, '_blank', 'noopener,noreferrer');
        } catch {
            notify.warn('Открытие не удалось, следуйте инструкции ниже');
            transition('fallback');
            return;
        }

        transition('confirming');
    };

    const handleFallback = () => transition('fallback');

    return (
        <div className="import-link-root">
            {state === 'idle' && (
                <div className="import-link-idle">
                    <button className="vpn-import-btn" onClick={handleImport} disabled={loading}>
                        {loading ? 'Подготовка ссылки...' : 'Нажмите, чтобы добавить конфигурацию в приложение'}
                    </button>
                    <div className="import-link-hint">
                        После нажатия приложение должно открыться и предложить добавить конфигурацию
                    </div>
                </div>
            )}

            {state === 'confirming' && (
                <div className="import-confirm-block">
                    <div className="import-confirm-question">
                        Приложение открылось и предложило добавить конфигурацию?
                    </div>
                    <div className="import-confirm-actions">
                        <button className="import-confirm-btn yes" onClick={() => transition('idle')}>
                            ✓ Да
                        </button>
                        <button className="import-confirm-btn no" onClick={handleFallback}>
                            ✗ Нет
                        </button>
                    </div>
                </div>
            )}

            {state === 'fallback' && fallbackCopyInstruction.length > 0 && (
                <div className="import-fallback-block">
                    {fallbackCopyInstruction.map((node, index) => (
                        <div key={(node as React.ReactElement).key ?? `fallback-${index}`}>
                            {React.cloneElement(node as React.ReactElement<any>, { onExpandContent })}
                        </div>
                    ))}
                    <button type="button" className='vpn-setup-tips' onClick={() => transition('idle')}>
                        вернуться к автоматической настройке
                    </button>
                </div>
            )}
        </div>
    );
};

export const StepLaunchInstruction: React.FC<{
    instruction: string;
    img?: string;
    onOpenImage?: (img: string) => void;
    onExpandContent?: () => void;
}> = ({ instruction, img, onOpenImage, onExpandContent }) => {
    return (
        <div>
            <div>{instruction}</div>
            <StepImagePreview img={img} onOpenImage={onOpenImage} onExpandContent={onExpandContent} />
        </div>
    );
};

const StepImagePreview: React.FC<{
    img?: string;
    onOpenImage?: (img: string) => void;
    onExpandContent?: () => void;
}> = ({ img, onOpenImage, onExpandContent }) => {
    if (!img) {
        return null;
    }

    return (
        <button
            type="button"
            className="vpn-step-image-preview"
            onClick={() => onOpenImage?.(img)}
            aria-label="Открыть изображение в полном размере"
        >
            <img
                src={img}
                alt="Иллюстрация к шагу настройки"
                className="vpn-step-image-preview-thumb"
                loading="lazy"
                onLoad={() => onExpandContent?.()}
            />
            <span className="vpn-step-image-preview-hint">
                <ZoomInIcon size={16} />
                Нажмите, чтобы увеличить
            </span>
        </button>
    );
};
